import React, { useState } from 'react';
import './SpLogTable.css';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '—';
    const s = new Date(startDate + 'T00:00:00');
    const month = MONTHS[s.getMonth()];
    const dayStart = s.getDate();
    const year = s.getFullYear();
    if (!endDate || endDate === startDate) return `${month} ${dayStart}, ${year}`;
    const e = new Date(endDate + 'T00:00:00');
    if (e.getFullYear() !== year) {
        return `${month} ${dayStart}, ${year} – ${MONTHS[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
    }
    if (s.getMonth() === e.getMonth()) return `${month} ${dayStart}–${e.getDate()}, ${year}`;
    return `${month} ${dayStart} – ${MONTHS[e.getMonth()]} ${e.getDate()}, ${year}`;
};

// spValue is the per-unit award; quantity multiplies it; newPlayerBonus doubles it (1 + 1).
const spTypeSubtotal = (t) => t.spValue * t.quantity * (1 + (t.newPlayerBonus ? 1 : 0));

// Main table column widths: Date, Event, SP Earned, Deaths, Bottled Deaths,
// Debt Incurred, SP Debt Repaid, SP Debt, Total SP, Effective SP. The
// numeric columns only ever hold a couple digits, so they're kept narrow
// (paired with a capped table max-width in CSS) rather than spread out
// across the full width.
const COLUMN_WIDTHS = ['12%', '20%', '8%', '6%', '9%', '9%', '9%', '7%', '9%', '11%'];

// Breakdown table has its own compact layout. The leading spacer is wider than
// the Date column above it, so Type starts indented past where Event starts —
// a clearer "this is a child of the row above" cue than lining up exactly.
// Qty/SP Each/Bonus/Subtotal/Notes pack together immediately afterward rather
// than being spread out to match specific columns further right — spacing
// them out to align under Debt/Total SP columns left an ugly empty gap.
const BREAKDOWN_COLUMN_WIDTHS = ['18%', '20%', '13%', '12%', '12%', '13%', '12%'];

const SpBreakdown = ({ spTypes }) => {
    if (!spTypes || spTypes.length === 0) {
        return <p className="sp-log-status sp-log-breakdown-empty">No SP type detail for this event.</p>;
    }
    return (
        <table className="sp-log-breakdown-table">
            <colgroup>
                {BREAKDOWN_COLUMN_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            <thead>
                <tr>
                    <th className="sp-log-breakdown-spacer" />
                    <th>Type</th>
                    <th className="sp-log-center">Qty</th>
                    <th className="sp-log-center">SP Each</th>
                    <th className="sp-log-center">Bonus</th>
                    <th className="sp-log-center">Subtotal</th>
                    <th className="sp-log-center">Notes</th>
                </tr>
            </thead>
            <tbody>
                {spTypes.map((t, i) => (
                    <tr key={`${t.spTypeId}-${i}`}>
                        <td className="sp-log-breakdown-spacer" />
                        <td>{t.spTypeName}</td>
                        <td className="sp-log-center">{t.quantity}</td>
                        <td className="sp-log-center">{t.spValue}</td>
                        <td className="sp-log-center">{t.newPlayerBonus ? '2×' : '—'}</td>
                        <td className="sp-log-center">{spTypeSubtotal(t)}</td>
                        <td className="sp-log-center sp-log-notes-cell">{t.notes || '—'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const SpLogTable = ({ spLog, loading }) => {
    const [expandedEventId, setExpandedEventId] = useState(null);

    if (loading) return <p className="sp-log-status">Loading SP log…</p>;
    if (!spLog || spLog.length === 0) return <p className="sp-log-status">No SP history yet.</p>;

    return (
        <table className="sp-log-table">
            <colgroup>
                {COLUMN_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Event</th>
                    <th className="sp-log-num">SP Earned</th>
                    <th className="sp-log-num">Deaths</th>
                    <th className="sp-log-num">Bottled Deaths</th>
                    <th className="sp-log-num">Debt Incurred</th>
                    <th className="sp-log-num">SP Debt Repaid</th>
                    <th className="sp-log-num">SP Debt</th>
                    <th className="sp-log-num">Total SP</th>
                    <th className="sp-log-num">Effective SP</th>
                </tr>
            </thead>
            <tbody>
                {spLog.map(row => {
                    const expanded = expandedEventId === row.eventId;
                    return (
                        <React.Fragment key={row.eventId}>
                            <tr
                                className={`sp-log-row${expanded ? ' sp-log-row--expanded' : ''}`}
                                onClick={() => setExpandedEventId(prev => prev === row.eventId ? null : row.eventId)}
                            >
                                <td className="sp-log-date-cell">{formatDateRange(row.eventStartDate, row.eventEndDate)}</td>
                                <td>
                                    {row.eventType}
                                    {row.site && <span className="sp-log-site"> · {row.site}</span>}
                                </td>
                                <td className="sp-log-num">{row.eventSp > 0 ? `+${row.eventSp}` : row.eventSp}</td>
                                <td className="sp-log-num">{row.deaths || 0}</td>
                                <td className="sp-log-num">{row.spiritBottleDeaths || 0}</td>
                                <td className="sp-log-num">{row.incurredDebt || 0}</td>
                                <td className="sp-log-num">{row.reductionAmt || 0}</td>
                                <td className="sp-log-num">{row.spDebtAfterEvent || 0}</td>
                                <td className="sp-log-num">{row.totalSpAfterEvent}</td>
                                <td className="sp-log-num">{row.effectiveSpAfterEvent}</td>
                            </tr>
                            {expanded && (
                                <tr className="sp-log-breakdown-row">
                                    <td colSpan={10}>
                                        <SpBreakdown spTypes={row.spTypes} />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );
};

export default SpLogTable;
